/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable} from '@angular/core';

import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';

import {ResponseChartBox} from './dashboard.interfaces';
import {
  ABSENCES_BY_FAMILY,
  CONTRACTS_TO_CHECK,
  CONTRACT_TYPES,
  FTE_AND_HEADCOUNT,
  HIRE_TERMINATION,
  PAYROLL_INPUT_SYNC_STATUS,
  PAYROLL_STATUS,
  PAYROLL_VALIDATION_PROGRESS,
  PERSON_MASTER_DATA_V2_SYNC_STATUS,
  PERSON_MASTER_DATA_V3_SYNC_STATUS,
  TERMINATION_REASONS,
  TURNOVER,
  VACANT_POSITIONS,
  VALIDATION_PLAN_BY_FAMILY,
  VALIDATION_PLAN_BY_TYPE
} from './dashboard.mock';

import {REQUEST_OPTIONS} from '../app.constants';
import {ApiService} from '../shared/api';

export const SEARCH_CONTEXT_CARDS: ReadonlySet<string> = new Set([
  'CONTRACTS_TO_CHECK',
  'PAYROLL_SYNC_STATUS',
  'SDC3_SYNC_STATUS',
  'PAYROLL_INPUT_SYNC_STATUS',
  'PERSON_MASTER_DATA_V2_SYNC_STATUS',
  'PERSON_MASTER_DATA_V3_SYNC_STATUS'
]);

@Injectable()
export class DashboardService {

  public static CARD_FALLBACKS: { [key: string]: string } = {
    'SDC3_SYNC_STATUS': 'PERSON_MASTER_DATA_V3_SYNC_STATUS',
    'PAYROLL_SYNC_STATUS': 'PERSON_MASTER_DATA_V2_SYNC_STATUS'
  };

  constructor(private api: ApiService) {
  }

  public getDashboardConfig(): Observable<any> {
    return this.api.get('/ga/api/v1/dashboard/getDashboardConfig',
      {requestOptions: {disableCache: true}});
  }

  public getCurrencyByCliReg(cliReg: number): Observable<string> {
    return this.api.get(`/repository/api/v1/cliReg/${cliReg}/currency`, REQUEST_OPTIONS.RESPONSE_TYPE.TEXT);
  }

  public getChart(type: string, index: number, isRefresh: boolean = false, isFake: boolean = false,
                  payType: string = null): Observable<any> {
    return isFake
      ? of(this.getFakeChart(type))
      : this.api.get(
        `/ga/api/v1/dashboard/${isRefresh ? 'refresh' : 'getCard'}?cardType=${type}&index=${index}`
        + (payType ? `&pay-type=${payType}` : ''),
        {requestOptions: {disableCache: true}}
      ).pipe(
        map((response: any) => response ? {...response, isFake: false} : response),
        catchError(() => of(undefined))
      );
  }

  public updateChartConfigs(config: any): Observable<void> {
    return this.api.post('/ga/api/v1/dashboard/updateConfig', config);
  }

  public getChartList(): Observable<any> {
    return this.api.get('/ga/api/v1/dashboard/getPermittedCards');
  }

  /**
   * Return a fake chart used to display an example in edit pane
   */
  public getFakeChart(type: string): ResponseChartBox {
    let chartBox: ResponseChartBox;
    switch (type) {
      case 'HIRE_TERMINATION':
        chartBox = HIRE_TERMINATION;
        break;
      case 'TURNOVER':
        chartBox = TURNOVER;
        break;
      case 'CONTRACT_TYPES':
        chartBox = CONTRACT_TYPES;
        break;
      case 'TERMINATION_REASONS':
        chartBox = TERMINATION_REASONS;
        break;
      case 'FTE_AND_HEADCOUNT':
        chartBox = FTE_AND_HEADCOUNT;
        break;
      case 'ABSENCES_BY_FAMILY':
        chartBox = ABSENCES_BY_FAMILY;
        break;
      case 'PAYROLL_STATUS':
        chartBox = PAYROLL_STATUS;
        break;
      case 'PAYROLL_VALIDATION_PROGRESS':
        chartBox = PAYROLL_VALIDATION_PROGRESS;
        break;
      case 'VALIDATION_PLAN_BY_TYPE':
        chartBox = VALIDATION_PLAN_BY_TYPE;
        break;
      case 'VALIDATION_PLAN_BY_FAMILY':
        chartBox = VALIDATION_PLAN_BY_FAMILY;
        break;
      case 'VACANT_POSITIONS':
        chartBox = VACANT_POSITIONS;
        break;
      case 'CONTRACTS_TO_CHECK':
        chartBox = CONTRACTS_TO_CHECK;
        break;
      case 'PAYROLL_SYNC_STATUS':
      case DashboardService.CARD_FALLBACKS['PAYROLL_SYNC_STATUS']:
        chartBox = PERSON_MASTER_DATA_V2_SYNC_STATUS;
        break;
      case 'SDC3_SYNC_STATUS':
      case DashboardService.CARD_FALLBACKS['SDC3_SYNC_STATUS']:
        chartBox = PERSON_MASTER_DATA_V3_SYNC_STATUS;
        break;
      case 'PAYROLL_INPUT_SYNC_STATUS':
        chartBox = PAYROLL_INPUT_SYNC_STATUS;
        break;
      default:
        chartBox = {
          cardType: '',
          indexNumber: 0,
          params: null,
          isFake: true
        };
    }
    return chartBox;
  }
}
